import MidiEffect from '../MidiEffect.js';
import Tone from 'tone';
import {isNumber} from 'utils/IsNumber.js';
import {AbstractParameter} from 'parameters/AbstractParameter.js';
import {saveParameterToJson, loadParameterFromJson} from 'parameters/parameterJsonFunctions.js';
import {frequencyToTranspose, equalTemperment} from './utils.js';
import {
    AL_FARABI_25_TONE_10_FRET_OUD_3_2,
    AL_FARABI_25_TONE_10_FRET_OUD_PERSIAN_CHARAGAH,
} from './scales.js';
const DEFAULT_NOTE = Tone.Frequency("C4");


export default class ScaleEffect extends MidiEffect {
    constructor(track) {
        super('Scale', track);

        // a mapping between transpose number and
        // transpose offset
        // When user plays note "+4", it first gets
        // thrown into the algo func. (4) => 4
        // And then whatever value is in offsets[4] gets
        // added to this
        this.scaleParameter = new AbstractParameter(
            'scale',
            this, (o, x) => [],
            {
                defaultValue: AL_FARABI_25_TONE_10_FRET_OUD_PERSIAN_CHARAGAH
            });

        this.registerParameter(this.scaleParameter);

        this.referencePitchParameter = new AbstractParameter(
            'referencePitch',
            this, (o, x) => [],
            {
                defaultValue: 195.99771799087463
            });
        this.registerParameter(this.referencePitchParameter);

        this.subsetParameter = new AbstractParameter(
            'subset',
            this, (o, x) => [],
            {
                defaultValue: [true, true, true, true, true, true, true]
            });

        this.registerParameter(this.subsetParameter);
    }

    get documentation() {
        return [
            "Forces the notes to a scale. The scale can be defined using ratios where 1 is the base note, and 2 is the octave.",
            "Can drag and drop a scala file with custom micro-tunings."
        ];
    }

    set subset(x) {
        this.subsetParameter.set(x);
    }

    get subset() {
        return this.subsetParameter.get();
    }

    get scale() {
        return this.scaleParameter.get(); 
    }

    set scale(x) {
        let subset = [];
        for (let i in x) {
            subset[i] = true;
        }
        this.subset = subset;
        this.scaleParameter.set(x);
    }

    get referencePitch() {
        return Tone.Frequency(this.referencePitchParameter.get());
    }

    output() {
        for (let i in this.triggers) {
            if (!this.triggers[i].stepData) {
                continue;
            }
            let duration = this.triggers[i].stepData.duration;
            let transpose = this.getTranspose(this.triggers[i].stepData.transpose);
            let stepData = this.getStep(
                this.triggers[i].stepData,
                transpose, 
                this.triggers[i].stepData.duration,
                true);

            stepData.duration = duration === null ? undefined : duration;
            this.triggers[i].stepData = stepData;
        }

        return this.triggers;
    }

    getTranspose(t) {
        // figure out what octave where in
        
        let scale = this.scale.filter((x, i) => this.subset[i]);
        let octave = Math.floor(t / 12);
        let offset = t < 0 ? (12 - (Math.abs(t) % 12)) % 12 : t % 12;
        let scaleNote = scale[Math.floor(scale.length * offset / 12.0)];
        try {
            scaleNote = eval(scaleNote);
        } catch (e) {
            return t;
        }
        // now we know which note of the scale

        let octavedNote = this.referencePitch.transpose(octave * 12)._val;
        let transpose = frequencyToTranspose(octavedNote * scaleNote);
        return transpose;
        // so we have to put it together
    }

    getJson() {
        let json = {
            ... super.getJson(),
            annotation: this.annotation
        };

        json = this.saveParameterToJson(json, 'scaleParameter', this.scaleParameter);
        json = this.saveParameterToJson(json, 'subsetParameter', this.subsetParameter);
        return json;
    }

    fromJson(json, copy) {
        super.fromJson(json, copy);
        this.scale = json.scale;
        if (json.subset) {
            this.subset = json.subset;
        }

        this.loadParameterFromJson(json, 'scaleParameter', this.scaleParameter);
        this.loadParameterFromJson(json, 'subsetParameter', this.subsetParameter);
        
        this.annotation = json.annotation;
    }

    getCopy(track, ignoreId=true) {
        let fx = new ScaleEffect(track);
        fx.fromJson(this.getJson(), ignoreId=true);
        return fx ;
    }
};

